import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export const L2CP_GROUP_ID = 'lentToCounterparties' as const;
export const L2CP_COLUMN_IDS = [
  'lentToCounterparties',
  'l2cpQuantity',
  'l2cpAmountEur',
  'l2cpRate',
  'l2cpGrossRate',
  'l2cpSnpDelta',
] as const;
export type L2CPColumnId = (typeof L2CP_COLUMN_IDS)[number];

export type ColumnConfig = {
  id: string;
  label: string;
  visible: boolean;
  groupId?: typeof L2CP_GROUP_ID;
};

export type ColumnPinSide = 'left' | 'right' | null;

export type InventoryColumnStore = {
  columns: ColumnConfig[];
  lentToCounterpartiesGroupExpanded: boolean;
  pinnedLeft: string[];
  pinnedRight: string[];
  setColumns: (columns: ColumnConfig[]) => void;
  setLentToCounterpartiesGroupExpanded: (expanded: boolean) => void;
  toggleColumnVisibility: (columnId: string) => void;
  showAllColumns: () => void;
  hideAllColumns: () => void;
  reorderColumns: (fromIndex: number, toIndex: number) => void;
  reorderColumnGroup: (groupId: typeof L2CP_GROUP_ID, toIndex: number) => void;
  reorderL2cpColumns: (fromId: string, toId: string) => void;
  pinColumn: (columnId: string, side: ColumnPinSide) => void;
  getPinState: (columnId: string) => ColumnPinSide;
  resetColumns: () => void;
  isColumnsDefault: () => boolean;
};

export const defaultColumns: ColumnConfig[] = [
  { id: 'legalEntity', label: 'Legal Entity', visible: true },
  { id: 'accountId', label: 'Account ID', visible: true },
  { id: 'counterparty', label: 'Counterparty', visible: true },
  { id: 'type', label: 'Type', visible: true },
  { id: 'sedol', label: 'SEDOL', visible: true },
  { id: 'isin', label: 'ISIN', visible: true },
  { id: 'symbolId', label: 'Symbol ID', visible: true },
  { id: 'exchange', label: 'Exchange', visible: true },
  { id: 'shortName', label: 'Short Name', visible: true },
  { id: 'price', label: 'Price', visible: true },
  { id: 'currency', label: 'Currency', visible: true },
  { id: 'quantity', label: 'Quantity (Trade Date)', visible: true },
  { id: 'settledQuantity', label: 'Settled Quantity', visible: true },
  { id: 'quantityT1', label: 'Expected Settled Quantity T+1', visible: true },
  { id: 'quantityT2', label: 'Expected Settled Quantity T+2', visible: true },
  { id: 'quantityTx', label: 'Expected Settled Quantity T+X', visible: true },
  { id: 'pendingSettlementQuantity', label: 'Pending Settlement (Due)', visible: true },
  { id: 'lentToCounterparties', label: 'Lent to Counterparties', visible: true, groupId: L2CP_GROUP_ID },
  { id: 'l2cpQuantity', label: 'Quantity', visible: false, groupId: L2CP_GROUP_ID },
  { id: 'l2cpAmountEur', label: 'Amount (in EUR)', visible: false, groupId: L2CP_GROUP_ID },
  { id: 'l2cpRate', label: 'Rate', visible: false, groupId: L2CP_GROUP_ID },
  { id: 'l2cpGrossRate', label: 'Gross-Rate', visible: false, groupId: L2CP_GROUP_ID },
  { id: 'l2cpSnpDelta', label: 'SnP-Delta', visible: false, groupId: L2CP_GROUP_ID },
  { id: 'pendingTransfers', label: 'Pending Transfers', visible: true },
  { id: 'leverageRate', label: 'Leverage Rate', visible: true },
  { id: 'valueEUR', label: 'Value, EUR', visible: true },
  { id: 'isLiquid', label: 'Is Liquid', visible: true },
  { id: 'brokerSettlementCounterparty', label: 'Primary Settlement CP', visible: true },
  { id: 'brokerAccountName', label: 'Primary Account Name', visible: true },
  { id: 'cimbEq2Quantity', label: 'Quantity CIMB (EQ2)', visible: true },
  { id: 'cimbSfQuantity', label: 'Quantity CIMB (SF)', visible: true },
  { id: 'cimbSf3Quantity', label: 'Quantity CIMB (SF3)', visible: true },
  { id: 'velocityLQuantity', label: 'Quantity Velocity (L)', visible: true },
  { id: 'velocityMQuantity', label: 'Quantity Velocity (M)', visible: true },
  { id: 'exDate', label: 'Ex-Date', visible: true },
  { id: 'caType', label: 'CA Type', visible: true },
  { id: 'caStatus', label: 'CA Status', visible: true },
  { id: 'benchmarkFee', label: 'Benchmark Fee', visible: true },
  { id: 'quantityOnLoan', label: 'Quantity On Loan', visible: true },
  { id: 'activeUtilisation', label: 'Active Utilisation', visible: true },
  { id: 'currencyBenchmark', label: 'Currency Benchmark', visible: true },
  { id: 'rebateRate', label: 'Rebate Rate', visible: true },
  { id: 'dailyPotentialEUR', label: 'Daily Potential, EUR', visible: true },
  { id: 'adtv30D', label: '30D ADTV', visible: true },
  { id: 'adtvMultiple', label: 'ADTV Multiple', visible: true },
  { id: 'adtvAdjustedPotentialEUR', label: 'ADTV-Adjusted-Potential, EUR', visible: true },
];

const defaultGroupExpanded = false;
export const DEFAULT_PINNED_LEFT = ['rowNumber'];
export const DEFAULT_PINNED_RIGHT: string[] = [];

export const useInventoryColumnStore = create<InventoryColumnStore>()(
  devtools(
  persist(
    (set, get) => ({
      columns: defaultColumns,
      lentToCounterpartiesGroupExpanded: defaultGroupExpanded,
      pinnedLeft: DEFAULT_PINNED_LEFT,
      pinnedRight: DEFAULT_PINNED_RIGHT,
      setColumns: (columns: ColumnConfig[]) => set({ columns }),
      setLentToCounterpartiesGroupExpanded: (expanded: boolean) =>
        set({ lentToCounterpartiesGroupExpanded: expanded }),
      toggleColumnVisibility: (columnId: string) =>
        set((state: InventoryColumnStore) => {
          const col = state.columns.find((c) => c.id === columnId);
          const becomingHidden = col?.visible === true;
          return {
            columns: state.columns.map((c) =>
              c.id === columnId ? { ...c, visible: !c.visible } : c
            ),
            // Auto-unpin when hiding (must be atomic in single set() call)
            pinnedLeft: becomingHidden
              ? state.pinnedLeft.filter((id) => id !== columnId)
              : state.pinnedLeft,
            pinnedRight: becomingHidden
              ? state.pinnedRight.filter((id) => id !== columnId)
              : state.pinnedRight,
          };
        }),
      showAllColumns: () =>
        set((state: InventoryColumnStore) => ({
          columns: state.columns.map((col: ColumnConfig) => ({ ...col, visible: true })),
        })),
      hideAllColumns: () =>
        set((state: InventoryColumnStore) => ({
          columns: state.columns.map((col: ColumnConfig) => ({ ...col, visible: false })),
          pinnedLeft: state.pinnedLeft.filter((id) => id === 'rowNumber'),
          pinnedRight: [],
        })),
      reorderColumns: (fromIndex: number, toIndex: number) =>
        set((state: InventoryColumnStore) => {
          const newColumns = [...state.columns];
          const fromCol = newColumns[fromIndex];
          const isFromGroup = fromCol?.groupId === L2CP_GROUP_ID;
          if (isFromGroup) {
            const groupStart = newColumns.findIndex((c) => c.groupId === L2CP_GROUP_ID);
            const groupSlice = newColumns.splice(groupStart, L2CP_COLUMN_IDS.length);
            let insertAt = toIndex;
            if (toIndex > groupStart) insertAt = Math.max(0, toIndex - L2CP_COLUMN_IDS.length);
            newColumns.splice(insertAt, 0, ...groupSlice);
            return { columns: newColumns };
          }
          const [movedColumn] = newColumns.splice(fromIndex, 1);
          newColumns.splice(toIndex, 0, movedColumn);
          return { columns: newColumns };
        }),
      reorderColumnGroup: (groupId: typeof L2CP_GROUP_ID, toIndex: number) =>
        set((state: InventoryColumnStore) => {
          if (groupId !== L2CP_GROUP_ID) return state;
          const newColumns = [...state.columns];
          const groupStart = newColumns.findIndex((c) => c.groupId === L2CP_GROUP_ID);
          if (groupStart === -1) return state;
          const groupSlice = newColumns.splice(groupStart, L2CP_COLUMN_IDS.length);
          let insertAt = toIndex;
          if (toIndex > groupStart) insertAt = Math.max(0, toIndex - L2CP_COLUMN_IDS.length);
          newColumns.splice(insertAt, 0, ...groupSlice);
          return { columns: newColumns };
        }),
      reorderL2cpColumns: (fromId: string, toId: string) =>
        set((state: InventoryColumnStore) => {
          const newColumns = [...state.columns];
          const fromIdx = newColumns.findIndex((c) => c.id === fromId);
          const toIdx = newColumns.findIndex((c) => c.id === toId);
          if (fromIdx === -1 || toIdx === -1) return state;
          // Ensure both are within the L2CP group
          const groupStart = newColumns.findIndex((c) => c.groupId === L2CP_GROUP_ID);
          const groupEnd = groupStart + L2CP_COLUMN_IDS.length;
          if (fromIdx < groupStart || fromIdx >= groupEnd) return state;
          if (toIdx < groupStart || toIdx >= groupEnd) return state;
          const [moved] = newColumns.splice(fromIdx, 1);
          newColumns.splice(toIdx, 0, moved);
          return { columns: newColumns };
        }),
      pinColumn: (columnId: string, side: ColumnPinSide) =>
        set((state: InventoryColumnStore) => {
          if (columnId === 'rowNumber') return state;
          // When pinning/unpinning L2CP group, also pin/unpin all inner columns
          const ids = columnId === L2CP_GROUP_ID ? [...L2CP_COLUMN_IDS] : [columnId];
          const newLeft = state.pinnedLeft.filter((id) => !ids.includes(id));
          const newRight = state.pinnedRight.filter((id) => !ids.includes(id));
          if (side === 'left') newLeft.push(...ids);
          if (side === 'right') newRight.push(...ids);
          return { pinnedLeft: newLeft, pinnedRight: newRight };
        }),
      getPinState: (columnId: string): ColumnPinSide => {
        const state = get();
        if (state.pinnedLeft.includes(columnId)) return 'left';
        if (state.pinnedRight.includes(columnId)) return 'right';
        return null;
      },
      resetColumns: () =>
        set({
          columns: defaultColumns,
          lentToCounterpartiesGroupExpanded: defaultGroupExpanded,
          pinnedLeft: DEFAULT_PINNED_LEFT,
          pinnedRight: DEFAULT_PINNED_RIGHT,
        }),
      isColumnsDefault: (): boolean => {
        const state: InventoryColumnStore = get();
        if (state.columns.length !== defaultColumns.length) return false;
        if (state.lentToCounterpartiesGroupExpanded !== defaultGroupExpanded) return false;
        const leftMatch = state.pinnedLeft.length === DEFAULT_PINNED_LEFT.length &&
          state.pinnedLeft.every((id, i) => id === DEFAULT_PINNED_LEFT[i]);
        const rightMatch = state.pinnedRight.length === DEFAULT_PINNED_RIGHT.length &&
          state.pinnedRight.every((id, i) => id === DEFAULT_PINNED_RIGHT[i]);
        if (!leftMatch || !rightMatch) return false;
        return state.columns.every((col: ColumnConfig, idx: number) =>
          col.id === defaultColumns[idx].id && col.visible === defaultColumns[idx].visible
        );
      },
    }),
    {
      name: 'inventory-columns-storage',
      version: 0,
      merge: (persistedState: unknown, currentState: InventoryColumnStore): InventoryColumnStore => {
        const persisted =
          typeof persistedState === 'object' && persistedState !== null
            ? (persistedState as Partial<InventoryColumnStore>)
            : undefined;
        if (!persisted?.columns?.length) return currentState;
        const persistedById = new Map(persisted.columns.map((c: ColumnConfig) => [c.id, c]));
        const defaultById = new Map(defaultColumns.map((c: ColumnConfig) => [c.id, c]));
        const mergedColumns: ColumnConfig[] = persisted.columns
          .filter((pc: ColumnConfig) => defaultById.has(pc.id))
          .map((pc: ColumnConfig) => ({ ...defaultById.get(pc.id)!, visible: pc.visible }));
        // Splice in every default column that was NOT in persisted state next to its
        // nearest already-persisted default neighbour: walk `defaultColumns` left from
        // the new column's index, the first id present in `mergedColumns` becomes its
        // predecessor (head if none). This way a column added in a later release (e.g.
        // CIMB SF3 next to CIMB SF) lands beside its sibling for returning users instead
        // of being stranded at the far right, while preserving the user's own reordering
        // of pre-existing columns. Mirrors the mtm-column-store smart merge (LR-433).
        for (let i = 0; i < defaultColumns.length; i++) {
          const dc = defaultColumns[i];
          if (persistedById.has(dc.id)) continue;
          let insertIdx = 0;
          for (let j = i - 1; j >= 0; j--) {
            const predIdx = mergedColumns.findIndex((m) => m.id === defaultColumns[j].id);
            if (predIdx !== -1) {
              insertIdx = predIdx + 1;
              break;
            }
          }
          mergedColumns.splice(insertIdx, 0, dc);
        }
        return {
          ...currentState,
          ...persisted,
          columns: mergedColumns,
          pinnedLeft: Array.isArray(persisted.pinnedLeft) ? persisted.pinnedLeft : DEFAULT_PINNED_LEFT,
          pinnedRight: Array.isArray(persisted.pinnedRight) ? persisted.pinnedRight : DEFAULT_PINNED_RIGHT,
        };
      },
    }
  ),
  { name: 'inventory-column-store', enabled: import.meta.env.DEV }
  )
);

